/**
 * Custom ChatTransport that sends messages over WebSocket to AgentCore Runtime.
 * Reuses a single persistent connection per session with:
 * - Auto-reconnect with exponential backoff on next sendMessages (1s base, 2× growth, 30s cap, jitter)
 * - Application-level heartbeat every 30s, idle or streaming (keeps ALB/proxy and the server heartbeat alive)
 * - Transparent reconnect between messages; mid-stream drops surface as errors (no re-send to avoid duplicates)
 */
import type { ChatTransport, UIMessage, UIMessageChunk } from 'ai';

import { getSignedWebSocketUrl } from './auth';
import config from 'src/config';

const CONNECTION_TIMEOUT_MS = 15_000;
const HEARTBEAT_INTERVAL_MS = 30_000;
const RECONNECT_BASE_MS = 1_000;
const RECONNECT_MAX_MS = 30_000;
const MAX_RECONNECT_ATTEMPTS = 5;
const SESSION_IDLE_TIMEOUT_MS = 10 * 60_000; // 10 min — stop keepalive, let AgentCore reclaim session; deferred while a reply streams

type Pending =
  | { kind: 'chunk'; value: UIMessageChunk }
  | { kind: 'error'; error: Error }
  | { kind: 'close' };

// The server sends an error frame (e.g. an idle timeout) with no finish frame after it, so it ends the stream too.
const STREAM_END_FRAME_TYPES = new Set(['finish', 'abort', 'error']);

function parseFrame (data: string): { chunk: UIMessageChunk; isStreamEnd: boolean; isPong: boolean; history: UIMessage[] | null } {
  try {
    const raw = JSON.parse(data) as Record<string, unknown>;
    if (raw.type === 'pong') return { chunk: null as unknown as UIMessageChunk, isStreamEnd: false, isPong: true, history: null };
    if (raw.type === 'history') return { chunk: null as unknown as UIMessageChunk, isStreamEnd: false, isPong: false, history: (raw.messages as UIMessage[]) ?? [] };
    if ('success' in raw && 'data' in raw) {
      const text = (raw.data as string) ?? (raw.error as string) ?? '';
      return { chunk: { type: 'text-delta', textDelta: text } as unknown as UIMessageChunk, isStreamEnd: false, isPong: false, history: null };
    }
    const isStreamEnd = STREAM_END_FRAME_TYPES.has(raw.type as string);
    return { chunk: raw as unknown as UIMessageChunk, isStreamEnd, isPong: false, history: null };
  } catch {
    return { chunk: { type: 'text-delta', textDelta: data } as unknown as UIMessageChunk, isStreamEnd: false, isPong: false, history: null };
  }
}

function backoffDelay (attempt: number): number {
  const delay = Math.min(RECONNECT_BASE_MS * 2 ** attempt, RECONNECT_MAX_MS);
  return delay * (0.5 + Math.random() * 0.5); // jitter ±50%
}

export class AgentCoreWebSocketTransport implements ChatTransport<UIMessage> {
  private ws: WebSocket | null = null;
  private chatId: string | null = null;
  private userId: string | null = null;
  private idToken: string | null = null;
  private entityId: string | null = null;
  private entityType: string | null = null;
  private connectingPromise: Promise<WebSocket> | null = null;
  private activeController: ReadableStreamDefaultController<UIMessageChunk> | null = null;
  private heartbeatTimer: ReturnType<typeof setInterval> | null = null;
  private idleTimer: ReturnType<typeof setTimeout> | null = null;
  private streaming = false;
  private reconnectAttempt = 0;
  private sessionExpired = false;
  private generation = 0;

  /** Callback invoked when the server sends a history frame with prior conversation messages. */
  onHistory: ((messages: UIMessage[]) => void) | null = null;

  /** Set the userId used for AgentCore session identity. Call before sending messages. */
  setUserId (userId: string): void {
    this.userId = userId;
  }

  /** Set the id-token JWT used to authenticate the AgentCore connection. */
  setIdToken (token: string): void {
    this.idToken = token;
  }

  /** Set or clear the entity context for agent-sessions tracking. */
  setEntity (entityId: string | null, entityType = 'campaign'): void {
    this.entityId = entityId;
    this.entityType = entityId ? entityType : null;
  }

  private startHeartbeat (ws: WebSocket): void {
    this.stopHeartbeat();
    this.heartbeatTimer = setInterval(() => {
      if (ws.readyState === WebSocket.OPEN) {
        ws.send(JSON.stringify({ type: 'ping', sessionId: this.chatId }));
      }
    }, HEARTBEAT_INTERVAL_MS);
  }

  private stopHeartbeat (): void {
    if (this.heartbeatTimer) {
      clearInterval(this.heartbeatTimer);
      this.heartbeatTimer = null;
    }
  }

  /**
   * Start/reset the idle timeout. After SESSION_IDLE_TIMEOUT_MS of no user messages, stop keepalive and mark session expired.
   * The close is deferred while a reply streams and re-armed when the stream ends; the server's WS_MAX_TURN_MS bounds the turn.
   */
  private resetIdleTimeout (): void {
    if (this.idleTimer) clearTimeout(this.idleTimer);
    this.idleTimer = setTimeout(() => {
      if (this.streaming) return;
      this.stopHeartbeat();
      if (this.ws) { this.ws.close(); this.ws = null; }
      this.sessionExpired = true;
    }, SESSION_IDLE_TIMEOUT_MS);
  }

  /** Leaves streaming state so the deferred session idle close re-arms; every stream-end path calls this. */
  private endStreaming (): void {
    this.streaming = false;
    this.resetIdleTimeout();
  }

  /** Restore idle-state handlers after a stream completes. */
  private installIdleHandlers (ws: WebSocket): void {
    // The server sends history right after the socket opens, which can arrive with no send in flight.
    ws.onmessage = (event: MessageEvent) => {
      const { history } = parseFrame(event.data as string);
      if (history !== null) this.onHistory?.(history);
    };
    ws.onerror = null;
    ws.onclose = () => {
      this.ws = null;
      this.stopHeartbeat();
    };
  }

  private async getConnection (chatId: string, generation: number): Promise<WebSocket> {
    if (this.ws && this.ws.readyState === WebSocket.OPEN && this.chatId === chatId) {
      return this.ws;
    }

    if (this.connectingPromise) return await this.connectingPromise;

    const connectionPromise = this.createConnection(chatId, generation);
    this.connectingPromise = connectionPromise;
    try {
      return await connectionPromise;
    } finally {
      if (this.connectingPromise === connectionPromise) this.connectingPromise = null;
    }
  }

  private async createConnection (chatId: string, generation: number): Promise<WebSocket> {
    if (generation !== this.generation) throw new Error('Aborted');
    if (this.ws) {
      this.ws.onclose = null;
      this.ws.onerror = null;
      this.ws.onmessage = null;
      this.ws.close();
      this.ws = null;
    }
    this.stopHeartbeat();
    this.streaming = false;

    const isLocal = (config.agentCoreWsUrl as string).startsWith('ws://localhost');
    const wsUrl = isLocal
      ? `${config.agentCoreWsUrl}?sessionId=${chatId}${this.userId ? `&userId=${encodeURIComponent(this.userId)}` : ''}${this.idToken ? `&X-Amzn-Bedrock-AgentCore-Runtime-Custom-IdToken=${encodeURIComponent(this.idToken)}` : ''}`
      : await getSignedWebSocketUrl(chatId, this.userId ?? chatId, this.idToken ?? undefined);

    if (generation !== this.generation) throw new Error('Aborted');
    const ws = new WebSocket(wsUrl);

    await new Promise<void>((resolve, reject) => {
      const timeout = setTimeout(() => {
        ws.close();
        reject(new Error('WebSocket connection timed out'));
      }, CONNECTION_TIMEOUT_MS);

      ws.onopen = () => { clearTimeout(timeout); resolve(); };
      ws.onerror = () => { clearTimeout(timeout); ws.close(); reject(new Error('WebSocket connection failed')); };
    });

    if (generation !== this.generation) {
      ws.onclose = null;
      ws.onerror = null;
      ws.onmessage = null;
      ws.close();
      throw new Error('Aborted');
    }

    this.ws = ws;
    this.chatId = chatId;
    this.reconnectAttempt = 0;
    this.startHeartbeat(ws);
    this.installIdleHandlers(ws);

    return ws;
  }

  private async reconnect (chatId: string, generation: number): Promise<WebSocket> {
    this.reconnectAttempt = 0;
    while (this.reconnectAttempt < MAX_RECONNECT_ATTEMPTS) {
      if (generation !== this.generation) throw new Error('Aborted');
      const delay = backoffDelay(this.reconnectAttempt);
      this.reconnectAttempt++;
      console.info(`[ChatTransport] reconnecting (attempt ${this.reconnectAttempt}) in ${Math.round(delay)}ms`);
      await new Promise(resolve => setTimeout(resolve, delay));
      if (generation !== this.generation) throw new Error('Aborted');
      try {
        return await this.createConnection(chatId, generation);
      } catch (error) {
        if (generation !== this.generation) throw error;
      }
    }
    throw new Error('WebSocket reconnection failed after max attempts');
  }

  async sendMessages (options: {
    chatId: string;
    messages: UIMessage[];
    abortSignal: AbortSignal | undefined;
  }): Promise<ReadableStream<UIMessageChunk>> {
    if (options.abortSignal?.aborted) throw new Error('Aborted');
    if (this.sessionExpired) throw new Error('Session expired. Please start a new conversation.');

    const generation = this.generation;
    this.resetIdleTimeout();

    let ws: WebSocket;
    try {
      ws = await this.getConnection(options.chatId, generation);
    } catch {
      if (options.abortSignal?.aborted === true || generation !== this.generation) throw new Error('Aborted');
      ws = await this.reconnect(options.chatId, generation);
    }

    if (options.abortSignal?.aborted === true || generation !== this.generation) throw new Error('Aborted');

    // Close any orphaned previous stream controller
    if (this.activeController) {
      try { this.activeController.close(); } catch { /* already closed */ }
      this.activeController = null;
    }

    const payload = {
      messages: options.messages
        .map(m => ({
          id: m.id,
          role: m.role,
          content: m.parts
            ?.filter((p): p is { type: 'text'; text: string } => p.type === 'text')
            .map(p => p.text)
            .join('') ?? '',
        }))
        .filter(m => m.content.trim().length > 0),
      sessionId: options.chatId,
      ...(this.entityId && { entityId: this.entityId, entityType: this.entityType ?? 'campaign' }),
    };

    // Buffer pattern: install handlers BEFORE sending to prevent dropped frames
    const buffer: Pending[] = [];
    let controller: ReadableStreamDefaultController<UIMessageChunk> | null = null;
    let streamDone = false;

    const deliver = (item: Pending): void => {
      if (streamDone) return;
      if (item.kind !== 'chunk') streamDone = true;
      if (!controller) { buffer.push(item); return; }
      if (item.kind === 'chunk') { try { controller.enqueue(item.value); } catch { /* stream already closed */ } } else if (item.kind === 'error') { try { controller.error(item.error); } catch { /* already closed */ } } else { try { controller.close(); } catch { /* already closed */ } }
    };

    const installStreamHandlers = (socket: WebSocket): void => {
      socket.onmessage = (event: MessageEvent) => {
        const { chunk, isStreamEnd, isPong, history } = parseFrame(event.data as string);
        if (isPong) return; // heartbeat response, ignore
        if (history !== null) { this.onHistory?.(history); return; }
        deliver({ kind: 'chunk', value: chunk });
        if (isStreamEnd) {
          this.endStreaming();
          this.installIdleHandlers(socket);
          deliver({ kind: 'close' });
        }
      };

      socket.onerror = () => {
        this.endStreaming();
        deliver({ kind: 'error', error: new Error('WebSocket error during streaming') });
      };

      socket.onclose = (event: CloseEvent) => {
        this.ws = null;
        this.stopHeartbeat();
        this.endStreaming();
        if (event?.code && event.code !== 1000) {
          console.warn('[ChatTransport] WebSocket closed unexpectedly', { code: event.code, reason: event.reason });
          deliver({ kind: 'error', error: new Error(`WebSocket closed unexpectedly: ${event.code} ${event.reason}`) });
        } else {
          deliver({ kind: 'close' });
        }
      };
    };

    installStreamHandlers(ws);
    this.streaming = true;

    const onAbort = (): void => {
      if (!this.streaming) return;
      this.endStreaming();
      if (this.ws) this.ws.close();
      deliver({ kind: 'close' });
    };
    options.abortSignal?.addEventListener('abort', onAbort, { once: true });

    ws.send(JSON.stringify(payload));

    return new ReadableStream<UIMessageChunk>({
      start: (c) => {
        controller = c;
        this.activeController = c;
        for (const item of buffer) {
          if (item.kind === 'chunk') { try { c.enqueue(item.value); } catch { break; } } else if (item.kind === 'error') { c.error(item.error); return; } else { c.close(); return; }
        }
        buffer.length = 0;
      },
      cancel: () => {
        options.abortSignal?.removeEventListener('abort', onAbort);
      },
    });
  }

  async reconnectToStream (): Promise<ReadableStream<UIMessageChunk> | null> {
    return null;
  }

  /** Close the WebSocket and clean up all timers. Resets state so transport can be reused. */
  disconnect (): void {
    this.generation++;
    this.connectingPromise = null;
    this.stopHeartbeat();
    if (this.idleTimer) { clearTimeout(this.idleTimer); this.idleTimer = null; }
    if (this.activeController) {
      try { this.activeController.close(); } catch { /* already closed */ }
      this.activeController = null;
    }
    if (this.ws) {
      this.ws.onclose = null;
      this.ws.onerror = null;
      this.ws.onmessage = null;
      this.ws.close();
      this.ws = null;
    }
    this.chatId = null;
    this.streaming = false;
    this.sessionExpired = false;
  }
}
